<template>
    <div
        v-if="isMounted"
        v-can="'settings_status_mapper.access'"
        class="mapping_statuses"
    >

        <div
            v-for="(groupedByCategorySettings, categorySlug) in settings"
            :key="categorySlug"
            class="mb-6"
        >
            <h3 class="mb-3 underline">{{ $t(`system_settings.category.${categorySlug}`) }}</h3>

            <ds-table
                :data="groupedByCategorySettings"
                :heads="heads"
                spacer
                :paginate="false"
                :view-items="[10, 15, 20]"
                :max-items="1000"
            >
                <template v-slot:slug="{slug}">
                    {{ $t(`system_settings.title.${slug.slug}`) }}
                </template>

                <template v-slot:title="{title}">
                    <div class="flex items-center">
                        <div class="mr-4 font-medium ">{{ $t(`system_settings.description.${title.slug}`) }}</div>
                    </div>
                </template>

                <template v-slot:value="{ value }">
                    <template v-if="value.type === 'boolean'">
                        <vs-switch
                            :checked="Boolean(value.value)"
                            color="success"
                            icon-pack="feather"
                            vs-icon-off="icon-x"
                            vs-icon-on="icon-check"
                            @click="changeStatus(value)"
                        />
                    </template>

                    <template v-else-if="isGroupStatusesSetting(value.slug)">
                        <div class="flex w-full items-center">
                            <v-select
                                v-model="value.value"
                                class="settings-select"
                                :options="groupStatusesOptions"
                                :placeholder="$t('global.Value')"
                                :clearable="true"
                                :calculate-position="withPopper"
                                multiple
                                appendToBody
                            />

                            <vs-button
                                class="ml-1"
                                :disabled="isLoader"
                                v-bind="$iconData('icon-save', 'fi-rr-disk')"
                                @click="changeStatus(value)"
                            />
                        </div>
                    </template>

                    <template v-else-if="isInvalidStatusesSetting(value.slug)">
                        <div class="flex w-full items-center">
                            <v-select
                                v-model="value.value"
                                class="settings-select"
                                :options="invalidStatusesOptions"
                                :placeholder="$t('global.Value')"
                                :clearable="true"
                                :calculate-position="withPopper"
                                multiple
                                appendToBody
                            />

                            <vs-button
                                class="ml-1"
                                :disabled="isLoader"
                                v-bind="$iconData('icon-save', 'fi-rr-disk')"
                                @click="changeStatus(value)"
                            />
                        </div>
                    </template>

                    <template v-else-if="isReconcileExtendedTypes(value.slug) && (value.type === 'array' || value.type === 'json')">
                        <div class="flex w-full items-center">
                            <v-select
                                v-model="value.value"
                                class="settings-select"
                                :options="reconcileExtendedTypes"
                                :placeholder="$t('global.Value')"
                                :clearable="true"
                                :calculate-position="withPopper"
                                multiple
                                appendToBody
                            />

                            <vs-button
                                class="ml-1"
                                :disabled="isLoader"
                                v-bind="$iconData('icon-save', 'fi-rr-disk')"
                                @click="changeStatus(value)"
                            />
                        </div>
                    </template>

                    <template v-else-if="isTeamSetting(value.slug) && (value.type === 'array' || value.type === 'json')">
                        <div class="flex w-full items-center">
                            <v-select
                                v-model="value.value"
                                class="settings-select"
                                :options="teamOptions"
                                :placeholder="$t('global.Value')"
                                :clearable="true"
                                :calculate-position="withPopper"
                                multiple
                                appendToBody
                            />

                            <vs-button
                                class="ml-1"
                                :disabled="isLoader"
                                v-bind="$iconData('icon-save', 'fi-rr-disk')"
                                @click="changeStatus(value)"
                            />
                        </div>
                    </template>

                    <template v-else>
                        <div class="flex w-full items-center">
                            <div class="relative flex-1">
                                <vs-input
                                    v-model="value.value"
                                    class="inputx w-full"
                                    :placeholder="$t('global.Value')"
                                    :type="value.type"
                                />

                                <vs-icon
                                    v-if="value.errorMessage"
                                    class="error-message"
                                    v-tooltip="value.errorMessage"
                                    v-bind="$iconData('icon-info', 'fi-rr-exclamation')"
                                />
                            </div>

                            <vs-button
                                class="ml-1"
                                :disabled="isLoader"
                                v-bind="$iconData('icon-save', 'fi-rr-disk')"
                                @click="changeStatus(value)"
                            />
                        </div>
                    </template>
                </template>
            </ds-table>
        </div>
    </div>
</template>

<script>
import ErrorHandler from '@/utils/errorHandler'
import send from '@/utils/fetch'
import EventBus from '@/store/eventBus'
import vSelect from 'vue-select'
import { vSelectPosition } from '@/utils/mixins'

export default {
    mixins: [vSelectPosition],
    components: {
        vSelect
    },
    data() {
        return {
            isMounted: false,
            isLoader: false,
            settings: {},
            teamOptions: [],
            reconcileExtendedTypes: [],
            groupStatusesOptions: [],
            invalidStatusesOptions: [],
            heads: [
                { name: 'global.title', key: 'slug', sortKey: 'slug', value: 'slug', display: true, width: '35%'},
                { name: 'global.Description', key: 'description', sortKey: 'description', value: 'title', display: true, width: '35%' },
                { name: 'global.Status', key: 'value', sortKey: 'value', value: 'value', display: true, width: '30%'}
            ]
        }
    },

    methods: {
        isOption(data) {
            return data.hasOwnProperty('label') && data.hasOwnProperty('value')
        },


        isGroupStatusesSetting(slug) {
            return ['lead_expiration_status_changeable_statuses_list'].includes(slug)
        },

        isInvalidStatusesSetting(slug) {
            return ['invalid_statuses'].includes(slug)
        },

        isReconcileExtendedTypes(slug) {
            return slug === 'reconcile_extended_for_types'
        },

        isTeamSetting(slug) {
            return slug === 'teams_with_required_lead_ip'
        },

        castValue(item) {
            switch (item.type) {
                case 'boolean':
                    return Number(item.value)
                case 'array':
                case 'json':
                    return this.parseJsonValue(item.value)
                default:
                    return item.value
            }
        },

        parseJsonValue(value) {
            if (Array.isArray(value) || (value && typeof value === 'object')) {
                return value
            }

            try {
                return JSON.parse(value)
            } catch (e) {
                return value
            }
        },

        transformValue(item) {
            const itemValue = this.castValue(item)

            if (this.isTeamSetting(item.slug) && Array.isArray(itemValue)) {
                const teamIds = itemValue.map(Number)

                return this.teamOptions.filter(option => teamIds.includes(option.value))
            }

            if ((this.isGroupStatusesSetting(item.slug) || this.isInvalidStatusesSetting(item.slug)) && typeof itemValue === 'string') {
                return itemValue
                    .split(',')
                    .map(value => value.trim())
                    .filter(Boolean)
            }

            return itemValue
        },

        normalizeSettings(items) {
            const typeMap = {
                string: 'text',
                integer: 'number'
            }

            return items.reduce((data, item) => {
                const normalizedItem = {
                    ...item,
                    type: typeMap[item.type] || item.type,
                    value: this.transformValue(item),
                    errorMessage: ''
                }

                const key = normalizedItem.category_slug

                if (!data[key]) {
                    data[key] = []
                }

                data[key].push(normalizedItem)

                return data
            }, {})
        },

        update(data) {
            this.clarityEvent('settings.system.system_action')

            EventBus.$emit('emitPopupEvent', {type: 'editSystemSettings', name: this.$t('modals.Edit_System_Settings'), data})
        },

        getRequestValue(item) {
            if (this.isInvalidStatusesSetting(item.slug) || this.isGroupStatusesSetting(item.slug)) {
                return Array.isArray(item.value) ? item.value.join(',') : item.value
            }

            if (Array.isArray(item.value)) {
                return item.value.map(value => this.isOption(value) ? value.value : value)
            }

            return this.isOption(item.value) ? item.value.value : item.value
        },

        async changeStatus(item) {
            const updateStatus = async value => {
                try {
                    await send(this.$url.settings_system.update, 'POST', {
                        params: { value, id: item.id }
                    })

                    item.errorMessage = ''

                    await this.get()
                    await this.$store.dispatch('GET_USER_DATA')
                } catch (e) {
                    if (e.response && e.response.data && e.response.data.result && e.response.data.result.errors) {
                        this.$set(item, 'errorMessage', e.response.data.result.errors[item.slug][0])
                        return
                    }

                    new ErrorHandler(e).send()
                }
            }

            if (item.type === 'boolean') {
                this.$vs.dialog({
                    type: 'confirm',
                    color: 'warning',
                    title: this.$t('confirm.Confirm_action'),
                    text: item.value ? this.$t('confirm.deactivateStatus') : this.$t('confirm.activateStatus'),
                    accept: () => updateStatus(item.value ? 0 : 1),
                    acceptText: item.value ? this.$t('buttons.Deactivate') : this.$t('buttons.Activate'),
                    cancelText: this.$t('global.Cancel')
                })

                return
            }

            await updateStatus(this.getRequestValue(item))
        },

        async loadTeamOptions() {
            try {
                const response = await send(this.$url.team.get, 'GET')
                    .then(response => response.data.result)

                this.teamOptions = response.map(item => ({
                    label: item.name,
                    value: item.id
                }))
            } catch (e) {
                new ErrorHandler(e).send()
            }
        },

        async getMeta() {
            try {
                const response = await send(this.$url.settings_system.meta, 'GET')
                    .then(response => response.data.result)

                this.groupStatusesOptions = response.group_statuses || []
                this.invalidStatusesOptions = response.invalid_statuses || []
                this.reconcileExtendedTypes = response.types || []
            } catch (e) {
                new ErrorHandler(e).send()
            }
        },

        async get() {
            try {
                this.isLoader = true

                const response = await send(this.$url.settings_system.get, 'GET')
                    .then(response => response.data.result)

                this.settings = this.normalizeSettings(response)

                this.isMounted = true
            } catch (e) {
                this.isMounted = false
                new ErrorHandler(e).send()
            } finally {
                this.isLoader = false
            }
        }
    },

    async mounted() {
        await Promise.all([
            this.loadTeamOptions(),
            this.getMeta()
        ])

        await this.get()

        EventBus.$on('updateSystemSettings', this.get)
    },

    beforeDestroy() {
        EventBus.$off('updateSystemSettings', this.get)
    }
}
</script>

<style scoped lang="scss">
.settings-select {
    flex: 1;
}

.error-message {
    position: absolute;
    top: -10px;
    left: -10px;
    z-index: 1;
    font-size: 20px;
    color: rgba(var(--vs-danger), 1);
    background-color: #fff;
    border-radius: 9999999px;
}
</style>
